import { inject } from '@angular/core';
import { setError, updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { getState, patchState, signalStore, type, withMethods, withState } from '@ngrx/signals';
import { entityConfig, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { withCallStatus, withEntitiesLoadingCall, withEntitiesRemoteFilter } from '@ngrx-traits/signals';
import { EMPTY, map } from 'rxjs';
import { delayOrNot } from '@bn/shared/entities/index';
import { getStoreEvents } from '@bn/shared/state-store';
import { DateTimeUtils, scrollToTop } from '@bn/shared/utils';
import { ShieldLogsItemModel } from '../models';
import { EventLogsService, GenericRequestResponse, GetEventLogsRequestParams } from '../openapi';

const config = entityConfig({
  entity: type<ShieldLogsItemModel>(),
  selectId: (log: ShieldLogsItemModel) => log.logId ?? '',
});
const defaultFilter: Omit<GetEventLogsRequestParams, ''> = {
  continuationToken: '',
  date: DateTimeUtils.currentIso8601(),
  shieldZoneId: 0, // something that is false, but type ok
};

// Define events for store actions
const events = getStoreEvents('SHIELD_EVENTS_FILTER');

export const AppShieldEventLogsStore = signalStore(
  withDevtools('Shield.EventLogs'),
  withState<{ shieldId: number | undefined }>({ shieldId: undefined }), // Add Shield Id
  // Represents the state for cursor-based pagination in the store. Filter is used here to pass cursor to api
  withState<{ endCursor: string | null; startCursor: string | null; cursorCache: string[] }>({ endCursor: null, startCursor: null, cursorCache: [] }),
  withEntities(config),
  withCallStatus({ ...config, initialValue: 'init', errorType: type<GenericRequestResponse>() }),
  withEntitiesRemoteFilter((store) => {
    return {
      ...config,
      defaultDebounce: 500, // default 300 triggers rate limit
      defaultFilter: {
        ...defaultFilter,
        shieldZoneId: getState(store).shieldId || 0, // something that is probably false, but type ok
      },
    };
  }),
  withMethods((store) => ({
    setShieldId: (shieldId: number): void => patchState(store, { shieldId }),
    forceResetEntitiesFilter: () => store.filterEntities({ filter: defaultFilter, forceLoad: true }),
  })),
  withEntitiesLoadingCall((store) => {
    return {
      ...config,
      fetchEntities: ({ entitiesFilter, shieldId }) => {
        const _shieldId = shieldId();

        // Return defaults if ShieldId is not set yet, but set shieldId before calling loadEntitiesPage() for the first time
        if (!_shieldId) return EMPTY;

        // manipulate cursor cache to get back in proper steps, have to do before api returns and adds a new continuationToken to state
        // cache musn't have current token if we are going back to latest
        const currentToken = entitiesFilter().continuationToken;
        const cursorCache = getState(store).cursorCache;
        let currentTokenCacheIndex = cursorCache.findIndex((token) => token === currentToken || currentToken === '');

        // if going forward, add token to cache
        if (currentTokenCacheIndex === -1 && (currentToken || currentToken === '')) cursorCache.push(currentToken);

        // reset to default, latest page or first open or date changed
        if (!currentToken && currentToken !== '') cursorCache.length = 0;

        // if going back, remove older tokens from cache
        if (currentTokenCacheIndex !== -1) cursorCache.splice(currentTokenCacheIndex + 1);

        currentTokenCacheIndex = cursorCache.findIndex((token) => token === currentToken || currentToken === '');

        // Silently set cache for pagination widget, since cache already holds current token, take one before to go back
        patchState(store, { startCursor: cursorCache[currentTokenCacheIndex - 1] ?? undefined, cursorCache });

        return inject(EventLogsService)
          .getEventLogs({
            ...entitiesFilter(),
            continuationToken: entitiesFilter().continuationToken ? entitiesFilter().continuationToken : '',
            shieldZoneId: _shieldId,
            // parse as utc time for api only here
            date: DateTimeUtils.toUtcTime(entitiesFilter().date ?? DateTimeUtils.dayjsNow())
              .utc()
              .format('MM-DD-YYYY'),
          })
          .pipe(
            delayOrNot(),
            map((res) => {
              // Silently set cache for pagination widget
              patchState(store, { endCursor: res.continuationToken || null });
              // Parse logs to new model
              return {
                entities: (res.logs ?? []).map((item) => <ShieldLogsItemModel>{ ...item, log: JSON.parse(item.log ?? '{}') }),
              };
            }),
          );
      },
      // Add custom state events
      call: (): void => updateState(store, events.FILTER.REQUEST),
      onSuccess: (res): void => {
        if ('entities' in res) {
          updateState(store, events.FILTER.SUCCESS, setAllEntities(res.entities, { ...config }));
          scrollToTop(200);
        }
      },
      onError: (error): void => {
        updateState(store, events.FILTER.ERROR);
        setError(error);
      },
    };
  }),
);
